import React, { useState, useEffect } from "react";
import { useNavigate as useNavigateHook } from "react-router-dom";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import ContractReviewTool from "@/ContractReviewTool";
import ContractReviewScanner from "@/ContractReviewScanner";
import DocumentComparison from "@/DocumentComparison";
import DueDiligence from "@/DueDiligence";
import LegalSummarisation from "@/LegalSummarisation";
import CheckoutSuccess from "@/CheckoutSuccess";
import RequestQuote from "@/RequestQuote";
import DepositionPrep from "@/DepositionPrep";
import InlineDocumentReview from "@/InlineDocumentReview";
import EmailResponseAssistant from "@/EmailResponseAssistant";
import InstructionAction from "@/InstructionAction";
import InstructionActionOutput from "@/InstructionActionOutput";
import Notetaker from "@/Notetaker";
import DocumentGeneration from "@/DocumentGeneration";
import LegalResearch from "@/LegalResearch";
import DebtRecoveryTools from "@/DebtRecoveryTools";
import Onboarding from "@/Onboarding";
import MatrixBackground from "@/MatrixBackground";
import FloatingChatWidget from "@/FloatingChatWidget";
import Footer from "@/Footer";
import TermsAndConditions from "@/TermsAndConditions";
import PrivacyPolicy from "@/PrivacyPolicy";
import Disclaimers from "@/Disclaimers";
import LawraLogo from "@/components/LawraLogo";
import LawraLogoStatic from "@/components/LawraLogoStatic";
import SiteHeader from "@/components/SiteHeader";
import ScrollToTop from "@/components/ScrollToTop";
import AccountSettings from "@/components/AccountSettings";
import ReferralProgram from "@/components/ReferralProgram";
import UseCases from "@/pages/UseCases";
import CaseStudy from "@/pages/CaseStudy";
import Pricing from "@/pages/Pricing";
import CreditCalculator from "@/pages/CreditCalculator";
import HowItWorks from "@/pages/HowItWorks";
import AboutUs from "@/pages/AboutUs";
import Tools from "@/pages/Tools";
import usePageSEO from "@/hooks/usePageSEO";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Toaster, toast } from "react-hot-toast";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Upload, FileText, Shield, CheckCircle, AlertTriangle, Scale, TrendingUp, BookOpen, FileEdit, MessageSquare, GitCompare, Search, Gavel, Menu, Lightbulb, Mail, Settings, Gift, Clipboard, Mic, Headset } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure Axios interceptor to attach Firebase ID token to all requests
axios.interceptors.request.use(
  async (config) => {
    try {
      const { auth } = await import('./firebase');
      const user = auth.currentUser;
      
      if (user) {
        // Get fresh ID token
        const idToken = await user.getIdToken();
        config.headers.Authorization = `Bearer ${idToken}`;
        console.log('✅ Axios Interceptor: Attached Firebase ID token to request');
      } else {
        console.warn('⚠️ Axios Interceptor: No Firebase user found, skipping token attachment');
      }
    } catch (error) {
      console.error('❌ Axios Interceptor: Error getting ID token:', error);
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Auth helper functions
const getUser = () => {
  const user = localStorage.getItem('user');
  return user ? JSON.parse(user) : null;
};

const setUserData = (user) => {
  localStorage.setItem('user', JSON.stringify(user));
};

const clearAuthData = () => {
  // Get user ID before clearing user data
  try {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const user = JSON.parse(userStr);
      const userId = user.id || user.firebase_uid;
      if (userId) {
        // Clear user-specific chat history
        localStorage.removeItem(`lawra_chat_history_${userId}`);
      }
    }
  } catch (e) {
    console.error('Error clearing chat history:', e);
  }
  
  // Clear user data
  localStorage.removeItem('user');
  
  // Also clear legacy non-user-specific chat history (cleanup)
  localStorage.removeItem('lawra_chat_history');
};

// Auth Context for managing Firebase auth state globally
const AuthContext = React.createContext({
  user: null,
  firebaseUser: null,
  loading: true,
  refreshUser: async () => {}
});

// Auth Provider Component
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      try {
        const { auth, onAuthStateChanged } = await import('./firebase');
        
        const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
          setFirebaseUser(fbUser);
          
          if (fbUser) {
            try {
              // Get ID token
              const idToken = await fbUser.getIdToken();
              
              // Sync with MongoDB
              const syncResponse = await axios.post(`${API}/auth/firebase-sync`, {
                firebase_uid: fbUser.uid,
                email: fbUser.email,
                full_name: fbUser.displayName || fbUser.email.split('@')[0]
              }, {
                headers: {
                  Authorization: `Bearer ${idToken}`
                }
              });
              
              setUser(syncResponse.data);
              setUserData(syncResponse.data);
            } catch (error) {
              console.error('Error syncing user:', error);
            }
          } else {
            setUser(null);
            clearAuthData();
          }
          
          setLoading(false);
        });
        
        return unsubscribe;
      } catch (error) {
        console.error('Error initializing auth:', error);
        setLoading(false);
      }
    };
    
    initAuth();
  }, []);

  const refreshUser = async () => {
    if (firebaseUser) {
      try {
        const idToken = await firebaseUser.getIdToken(true); // Force refresh
        const syncResponse = await axios.post(`${API}/auth/firebase-sync`, {
          firebase_uid: firebaseUser.uid,
          email: firebaseUser.email,
          full_name: firebaseUser.displayName || firebaseUser.email.split('@')[0]
        }, {
          headers: {
            Authorization: `Bearer ${idToken}`
          }
        });
        
        setUser(syncResponse.data);
        setUserData(syncResponse.data);
      } catch (error) {
        console.error('Error refreshing user:', error);
      }
    }
  };

  return (
    <AuthContext.Provider value={{ user, firebaseUser, loading, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Landing Page
const LandingPage = () => {
  const navigate = useNavigate();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [typedText, setTypedText] = useState('');
  const fullTitle = 'AI Assistant for Lawyers & Law Firms';

  useEffect(() => {
    let i = 0;
    const timer = setInterval(() => {
      setTypedText(fullTitle.slice(0, i + 1));
      i++;
      if (i >= fullTitle.length) clearInterval(timer);
    }, 85);
    return () => clearInterval(timer);
  }, []);

  const landingStructuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Lawra AI",
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Web",
    "offers": {
      "@type": "Offer",
      "price": "29",
      "priceCurrency": "GBP",
      "priceSpecification": {
        "@type": "UnitPriceSpecification",
        "price": "29",
        "priceCurrency": "GBP",
        "billingDuration": "P1M"
      }
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "ratingCount": "127"
    },
    "description": "AI-powered legal platform for lawyers. Contract review, due diligence, document generation, legal research & more. Save 70-80% time on routine legal tasks.",
    "featureList": [
      "Contract Review & Analysis",
      "Due Diligence",
      "Legal Document Generation",
      "Legal Research",
      "Deposition Preparation",
      "Document Comparison",
      "Legal Summarisation",
      "Email Response Assistant"
    ],
    "screenshot": `${window.location.origin}/logo-original.png`
  };

  usePageSEO({
    title: 'AI Legal Assistant for Lawyers & Law Firms | Save 70-80% Time',
    description: 'AI-powered legal platform for lawyers. Contract review, due diligence, document generation, legal research & more. Save 70-80% time on routine legal tasks with GPT-4o technology. Free trial available.',
    path: '/',
    structuredData: landingStructuredData,
  });

  return (
    <div className="landing-page">
      <MatrixBackground />
      {/* Announcement Bar */}
      <div className="announcement-bar" data-testid="announcement-bar">
        <p className="announcement-bar-text">
          50% off all plans — get early access to AI tools that will change how law firms operate forever.
          <a href="/pricing">Lock In Your Rate</a>
        </p>
      </div>

      {/* Fixed Header */}
      <div className="landing-header" style={{ top: '32px' }}>
        <div className="header-content-landing">
          <div className="header-left-group">
            <div className="header-logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
              <img src="/lawra-header-logo.png" alt="Lawra" className="header-logo-image" />
            </div>
            <button className="header-hamburger" onClick={() => setMobileNavOpen(!mobileNavOpen)} aria-label="Toggle menu">
              <Menu size={24} />
            </button>
            <nav className={`header-nav${mobileNavOpen ? ' mobile-open' : ''}`}>
              <button className="mobile-nav-close" onClick={() => setMobileNavOpen(false)} aria-label="Close menu">✕</button>
              <Button className="use-cases-nav-link" variant="ghost" onClick={() => { navigate('/use-cases'); setMobileNavOpen(false); }} data-testid="nav-use-cases">
                Use Cases
              </Button>
              <Button className="use-cases-nav-link" variant="ghost" onClick={() => { navigate('/tools'); setMobileNavOpen(false); }} data-testid="nav-tools">
                Tools
              </Button>
              <Button className="use-cases-nav-link" variant="ghost" onClick={() => { navigate('/how-it-works'); setMobileNavOpen(false); }} data-testid="nav-how-it-works">
                How It Works
              </Button>
              <Button className="use-cases-nav-link" variant="ghost" onClick={() => { navigate('/pricing'); setMobileNavOpen(false); }} data-testid="nav-pricing">
                Pricing
              </Button>
              <Button className="use-cases-nav-link" variant="ghost" onClick={() => { navigate('/credit-calculator'); setMobileNavOpen(false); }} data-testid="nav-calculator">
                Savings Calculator
              </Button>
              <Button className="use-cases-nav-link" variant="ghost" onClick={() => { navigate('/about'); setMobileNavOpen(false); }} data-testid="nav-about">
                About Us
              </Button>
              <Button className="sign-in-btn mobile-only-signin" onClick={() => { navigate('/auth'); setMobileNavOpen(false); }} style={{ marginTop: '1rem' }}>
                Sign In
              </Button>
            </nav>
          </div>
          <div className="header-right-actions">
            <a href="mailto:sales@lawra-ai.com?subject=Contact%20Enquiry%20-%20Lawra%20AI&body=Hi%20Lawra%20AI%20Team%2C%0A%0AI%20have%20a%20question%20about%20Lawra%20AI%3A%0A%0A%0A%0AThank%20you" className="support-icon-btn" data-testid="support-icon" aria-label="Contact Support">
              <Headset size={18} />
              <span className="support-online-dot" />
            </a>
            <Button 
              className="sign-in-btn"
              onClick={() => navigate('/auth')}
            >
              Sign In
            </Button>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <div className="hero-section">
        <div className="hero-content">
          <div className="hero-logo-container">
            <LawraLogo />
          </div>
          <div className="hero-split">
            <div className="hero-left">
              <div className="hero-avatar-wrap">
                <img src="/lawra-avatar.png" alt="Lawra AI Avatar" className="hero-avatar" />
              </div>
              <h2 className="section-title meet-lawra-title hero-meet-title">Meet Lawra</h2>
              <p className="hero-meet-subtitle">Your comprehensive AI legal assistant transforming how lawyers work</p>
            </div>
            <div className="hero-right">
              <h1 className="hero-title">{typedText}<span className="typing-cursor"></span></h1>
              <p className="hero-subtitle">
                All the tools a lawyer needs to do day-to-day tasks with more efficiency, saving time and money.
              </p>
              <div className="hero-buttons">
                <Button 
                  data-testid="try-now-btn"
                  className="try-now-btn" 
                  size="lg"
                  onClick={async () => {
                    try {
                      const { auth } = await import('./firebase');
                      if (auth.currentUser) {
                        navigate('/dashboard');
                      } else {
                        navigate('/onboarding');
                      }
                    } catch {
                      navigate('/onboarding');
                    }
                  }}
                >
                  Try Now for Free
                </Button>
              </div>
            </div>
          </div>
          <div className="hero-usps">
            <div className="hero-usp">✓ Improve efficiency by 70-80%</div>
            <div className="hero-usp">✓ Try immediately for free</div>
            <div className="hero-usp">✓ AI built for lawyers with accuracy</div>
            <div className="hero-usp">✓ Pricing from £14.99/m (50% off)</div>
          </div>
        </div>
      </div>

      {/* Trust Section - Above the fold risk reduction */}
      <div className="trust-section">
        <div className="trust-inner">
          <div className="trust-grid">
            <div className="trust-card">
              <div className="trust-icon-wrap">
                <Shield size={24} />
              </div>
              <h3>Is my data safe?</h3>
              <p>Your documents are encrypted in transit and at rest. We never use your data to train models. Client confidentiality is a foundation, not a feature.</p>
            </div>
            <div className="trust-card">
              <div className="trust-icon-wrap">
                <Scale size={24} />
              </div>
              <h3>Built for legal work?</h3>
              <p>Every tool is designed around how solicitors and lawyers actually work — legal language, clause structures, jurisdictional nuances, and compliance frameworks.</p>
            </div>
            <div className="trust-card">
              <div className="trust-icon-wrap">
                <CheckCircle size={24} />
              </div>
              <h3>Will this replace my judgment?</h3>
              <p>Never. Lawra accelerates your analysis and flags what matters — you make the decisions. AI assists, the lawyer advises. Every output is yours to review and refine.</p>
            </div>
            <div className="trust-card">
              <div className="trust-icon-wrap">
                <TrendingUp size={24} />
              </div>
              <h3>How fast will I see value?</h3>
              <p>Immediately. Upload a document or type a question and get structured results in under 60 seconds. No onboarding, no training, no configuration required.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="features-section">
        <div className="features-container">
          
          {/* Core Value Proposition */}
          <div className="value-proposition">
            <h2 className="section-title meet-lawra-title">What is Lawra AI?</h2>
            <p className="value-text">
              Lawra is an advanced AI platform built exclusively for legal professionals. 
              Our intelligent suite of tools transforms efficiency across every aspect of legal practice—from document review 
              and contract analysis to litigation preparation and client communication. 
              Save 70-80% of your time on routine tasks while maintaining exceptional accuracy and compliance standards.
            </p>
          </div>

          {/* Recent Client Stories */}
          <div className="client-stories-section">
            <h2 className="section-title meet-lawra-title">Recent Client Stories</h2>
            <div className="client-stories-grid">
              {[
                { slug: 'contract-review-commercial-law', title: 'Commercial Law Firm Reviewed 200+ Contracts in One Week', stat: '78%', statLabel: 'Time Saved', color: '#D4AF37' },
                { slug: 'due-diligence-ma-transaction', title: 'Due Diligence for a Multi-Million Pound M&A Deal', stat: '340+', statLabel: 'Docs Processed', color: '#00D4FF' },
                { slug: 'litigation-prep-personal-injury', title: 'Deposition Prep for a Complex Personal Injury Case', stat: '65%', statLabel: 'Prep Time Cut', color: '#FF6B35' },
                { slug: 'legal-research-criminal-defence', title: 'Case Law Research for a Criminal Defence Team', stat: '4x', statLabel: 'Faster Research', color: '#8B5CF6' },
                { slug: 'document-generation-family-law', title: 'Automating Documents for a Family Law Practice', stat: '90%', statLabel: 'Drafting Time Cut', color: '#10B981' },
                { slug: 'debt-recovery-automation', title: 'Scaling Debt Recovery Operations with AI', stat: '3x', statLabel: 'Volume Increase', color: '#F59E0B' },
                { slug: 'email-response-employment-law', title: 'Employment Law Firm Halved Response Times', stat: '52%', statLabel: 'Faster Responses', color: '#EC4899' },
                { slug: 'document-comparison-property-law', title: 'Lease Variations Across 300-Unit Portfolio', stat: '300+', statLabel: 'Leases Compared', color: '#06B6D4' },
                { slug: 'legal-summarisation-insurance-law', title: 'Summarising Policy Wordings for Lloyd\'s Dispute', stat: '85%', statLabel: 'Review Time Cut', color: '#7C3AED' },
                { slug: 'ai-chatbot-immigration-law', title: 'Visa Application Research for Multi-National Practice', stat: '40%', statLabel: 'Time Saved', color: '#14B8A6' },
                { slug: 'contract-review-technology-ip', title: 'SaaS & IP Licence Review for Tech Startup Portfolio', stat: '40+', statLabel: 'Companies Reviewed', color: '#3B82F6' },
                { slug: 'regulatory-compliance-financial-services', title: 'FCA Compliance Across Client Documentation', stat: '100%', statLabel: 'Compliance', color: '#EF4444' },
                { slug: 'inline-review-lease-negotiation', title: 'Annotating a 90-Page Commercial Lease', stat: '2hrs', statLabel: 'Review Time', color: '#F97316' },
                { slug: 'ask-lawra-client-consultations', title: 'Cutting Consultation Research Time with AI Chat', stat: '60%', statLabel: 'Time Saved', color: '#0EA5E9' },
                { slug: 'notetaker-client-instructions', title: 'Capturing Client Instructions from 50+ Weekly Calls', stat: '50+', statLabel: 'Calls Per Week', color: '#A855F7' },
                { slug: 'document-generation-corporate-restructure', title: 'Board Resolutions for a 15-Entity Restructure', stat: '80+', statLabel: 'Docs Generated', color: '#22C55E' },
                { slug: 'contract-review-nhs-procurement', title: 'NHS Contract Terms Across 25 Service Agreements', stat: '25', statLabel: 'Contracts Reviewed', color: '#DC2626' },
              ].map((story, i) => (
                <div key={i} className="client-story-row" onClick={() => navigate(`/use-cases/${story.slug}`)} data-testid={`client-story-${i}`}>
                  <div className="client-story-stat-wrap">
                    <span className="client-story-stat" style={{ color: story.color }}>{story.stat}</span>
                    <span className="client-story-stat-label">{story.statLabel}</span>
                  </div>
                  <span className="client-story-title">{story.title}</span>
                  <span className="client-story-link" style={{ color: story.color }}>Read Case Study →</span>
                </div>
              ))}
            </div>
          </div>

          {/* Capabilities Grid */}
          <div className="capabilities-section">
            <h2 className="section-title">Comprehensive Legal AI Tools</h2>
            <div className="features-grid">
              <div className="feature-card">
                <div className="feature-icon risk-icon">
                  <FileText size={32} />
                </div>
                <h3>Contract Intelligence</h3>
                <p>Advanced document review, clause analysis, risk assessment, and contract comparison with intelligent AI analysis</p>
              </div>
              
              <div className="feature-card">
                <div className="feature-icon clause-icon">
                  <Search size={32} />
                </div>
                <h3>Due Diligence & Research</h3>
                <p>Multi-document analysis for transactions, comprehensive legal research, and intelligent summarisation of complex materials</p>
              </div>
              
              <div className="feature-card">
                <div className="feature-icon compliance-icon">
                  <Gavel size={32} />
                </div>
                <h3>Litigation Support</h3>
                <p>Deposition preparation, witness statement analysis, and inline document review with AI-powered insights and commenting</p>
              </div>
              
              <div className="feature-card">
                <div className="feature-icon summary-icon">
                  <MessageSquare size={32} />
                </div>
                <h3>Practice Efficiency</h3>
                <p>Document generation, email response assistance, and instant legal guidance through conversational AI assistance</p>
              </div>
              
              <div className="feature-card">
                <div className="feature-icon risk-icon">
                  <Scale size={32} />
                </div>
                <h3>Multi-Sector Coverage</h3>
                <p>Specialized tools tailored for corporate, litigation, family, criminal, and commercial law practices with sector-specific insights</p>
              </div>
              
              <div className="feature-card">
                <div className="feature-icon clause-icon">
                  <TrendingUp size={32} />
                </div>
                <h3>Legal Analytics & Insights</h3>
                <p>Data-driven insights, performance metrics, and predictive analysis for case outcomes with intelligent reporting</p>
              </div>
            </div>
          </div>

          {/* How It Works Section */}
          <div className="how-it-works-section">
            <h2 className="section-title">How It Works</h2>
            <p className="section-subtitle">Get started in minutes, not hours</p>
            <div className="steps-container">
              <div className="step-card">
                <div className="step-number">1</div>
                <div className="step-icon">
                  <CheckCircle size={20} />
                </div>
                <h4>Simple Onboarding</h4>
                <p>Complete a quick 2-step setup to personalise Lawra for your practice area and jurisdiction</p>
              </div>
              
              <div className="step-arrow">→</div>
              
              <div className="step-card">
                <div className="step-number">2</div>
                <div className="step-icon">
                  <Upload size={20} />
                </div>
                <h4>Select Your Tool</h4>
                <p>Choose from our comprehensive suite of AI tools and upload your documents or enter your requirements</p>
              </div>
              
              <div className="step-arrow">→</div>
              
              <div className="step-card">
                <div className="step-number">3</div>
                <div className="step-icon">
                  <FileText size={20} />
                </div>
                <h4>Receive AI Analysis</h4>
                <p>Get detailed, actionable results in seconds—ready to review, edit, download, or act upon immediately</p>
              </div>
            </div>
          </div>

          {/* Stats Section */}
          <div className="stats-section">
            <div className="stat-card">
              <div className="stat-number">70-80%</div>
              <div className="stat-label">Time Saved</div>
              <p>Reduce hours of manual review to minutes with intelligent AI analysis</p>
            </div>
            <div className="stat-card">
              <div className="stat-number">10+</div>
              <div className="stat-label">Specialised Tools</div>
              <p>Comprehensive suite covering all aspects of legal practice</p>
            </div>
            <div className="stat-card">
              <div className="stat-number stat-number-single-line">Enterprise-Grade</div>
              <div className="stat-label">AI Technology</div>
              <p>Cutting-edge AI models optimised for legal accuracy and precision</p>
            </div>
          </div>

          {/* Trust Indicators */}
          <div className="trust-section">
            <h2 className="section-title">Built for Legal Professionals</h2>
            <div className="trust-grid">
              <div className="trust-card">
                <Shield size={28} />
                <h4>Secure & Confidential</h4>
                <p>Your data is protected with enterprise-grade security and confidentiality</p>
              </div>
              <div className="trust-card">
                <Scale size={28} />
                <h4>Jurisdiction-Aware</h4>
                <p>Tailored for UK legal standards with support for multiple jurisdictions</p>
              </div>
              <div className="trust-card">
                <CheckCircle size={28} />
                <h4>Compliance-Ready</h4>
                <p>Built to meet professional standards and regulatory requirements</p>
              </div>
              <div className="trust-card">
                <BookOpen size={28} />
                <h4>Sector-Specific</h4>
                <p>Customised tools for corporate, litigation, family, criminal, and commercial law</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Final CTA Section */}
      <div className="cta-section">
        <div className="cta-content">
          <h2>Ready to Transform Your Legal Practice?</h2>
          <p>Join forward-thinking law firms using AI to work smarter, faster, and more efficiently</p>
          <Button 
            data-testid="cta-btn"
            className="try-now-btn" 
            size="lg"
            onClick={async () => {
              try {
                const { auth } = await import('./firebase');
                if (auth.currentUser) {
                  navigate('/dashboard');
                } else {
                  navigate('/onboarding');
                }
              } catch {
                navigate('/onboarding');
              }
            }}
          >
            Start Your Free Trial
          </Button>
          <p className="cta-subtext">No credit card required • Instant access • 1 free analysis</p>
        </div>
      </div>
    </div>
  );
};

// Auth Page
const AuthPage = () => {
  const navigate = useNavigate();
  const location = window.location;
  const [isLogin] = useState(true); // Always login mode, sign up redirects to onboarding
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: ''
  });

  useEffect(() => {
    // Check if user is already authenticated via Firebase
    const checkAuth = async () => {
      const { auth, onAuthStateChanged } = await import('./firebase');
      
      onAuthStateChanged(auth, (firebaseUser) => {
        if (firebaseUser && firebaseUser.emailVerified) {
          navigate('/dashboard');
        }
      });
    };
    
    checkAuth();
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { auth, signInWithEmailAndPassword } = await import('./firebase');
      
      // Login with Firebase
      const userCredential = await signInWithEmailAndPassword(auth, formData.email, formData.password);
      const firebaseUser = userCredential.user;
      
      // Check if email is verified
      if (!firebaseUser.emailVerified && firebaseUser.email !== 'admin@lawraai.com') {
        toast.error('Please verify your email address. Check your inbox for verification link.');
        await auth.signOut();
        setLoading(false);
        return;
      }
      
      // Get ID token
      const idToken = await firebaseUser.getIdToken();
      
      // Sync with MongoDB for credits tracking
      const syncResponse = await axios.post(`${API}/auth/firebase-sync`, {
        firebase_uid: firebaseUser.uid,
        email: firebaseUser.email,
        full_name: firebaseUser.displayName || firebaseUser.email.split('@')[0]
      }, {
        headers: {
          Authorization: `Bearer ${idToken}`
        }
      });
      
      // Store user data in localStorage
      setUserData(syncResponse.data);
      
      toast.success('Login successful!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Auth error:', error);
      const errorMessage = error.code === 'auth/wrong-password' || error.code === 'auth/user-not-found'
        ? 'Invalid email or password'
        : error.code === 'auth/invalid-credential'
        ? 'Invalid email or password'
        : error.message || 'Authentication failed';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    try {
      const { auth, googleProvider, signInWithPopup } = await import('./firebase');
      
      console.log('Starting Google sign-in...');
      
      // Sign in with Google
      const result = await signInWithPopup(auth, googleProvider);
      const firebaseUser = result.user;
      
      console.log('Google sign-in successful:', firebaseUser.email);
      
      // Get ID token
      const idToken = await firebaseUser.getIdToken();
      
      // Sync with MongoDB
      const syncResponse = await axios.post(`${API}/auth/firebase-sync`, {
        firebase_uid: firebaseUser.uid,
        email: firebaseUser.email,
        full_name: firebaseUser.displayName || firebaseUser.email.split('@')[0]
      }, {
        headers: {
          Authorization: `Bearer ${idToken}`
        }
      });
      
      console.log('MongoDB sync successful');
      
      // Store user data
      setUserData(syncResponse.data);
      
      toast.success('Signed in with Google successfully!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Google sign-in error details:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      
      const errorMessage = error.code === 'auth/popup-closed-by-user'
        ? 'Sign-in cancelled'
        : error.code === 'auth/popup-blocked'
        ? 'Pop-up blocked. Please allow pop-ups for this site.'
        : error.code === 'auth/unauthorized-domain'
        ? 'This domain is not authorized. Please add it in Firebase Console → Authentication → Settings → Authorized domains'
        : error.code === 'auth/operation-not-allowed'
        ? 'Google Sign-In is not enabled. Please enable it in Firebase Console → Authentication → Sign-in method'
        : error.message || 'Google sign-in failed';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <MatrixBackground />
      <div className="auth-container">
        <Card className="auth-card">
          <CardHeader className="auth-header">
            <div className="auth-logo">
              <img src="/logo-128.png" alt="Lawra" className="logo-image-large" />
            </div>
            <div className="auth-lawra-title">
              <LawraLogo />
            </div>
            <CardDescription>
              Sign in to your account
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="auth-form">
              <div className="form-group">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  data-testid="email-input"
                  type="email"
                  placeholder="your@email.com"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                />
              </div>
              
              <div className="form-group">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  data-testid="password-input"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  required
                />
              </div>

              <Button 
                data-testid="auth-submit-btn"
                type="submit" 
                className="auth-submit-btn" 
                disabled={loading}
              >
                {loading ? 'Processing...' : 'Sign In'}
              </Button>
            </form>

            <div style={{ margin: '1.5rem 0', textAlign: 'center' }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '1rem',
                color: '#888'
              }}>
                <div style={{ flex: 1, height: '1px', background: '#333' }}></div>
                <span>or</span>
                <div style={{ flex: 1, height: '1px', background: '#333' }}></div>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              onClick={handleGoogleSignIn}
              disabled={loading}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.75rem',
                padding: '0.75rem',
                marginBottom: '1rem'
              }}
            >
              <svg width="18" height="18" viewBox="0 0 18 18">
                <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
                <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z"/>
                <path fill="#FBBC05" d="M3.964 10.71c-.18-.54-.282-1.117-.282-1.71s.102-1.17.282-1.71V4.958H.957C.347 6.173 0 7.548 0 9s.348 2.827.957 4.042l3.007-2.332z"/>
                <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/>
              </svg>
              Continue with Google
            </Button>

            <div className="auth-switch">
              <p>
                {isLogin ? "Don't have an account?" : "Already have an account?"}
                <button 
                  data-testid="toggle-auth-btn"
                  type="button" 
                  onClick={() => {
                    if (isLogin) {
                      // Redirect to onboarding for sign up
                      navigate('/onboarding');
                    } else {
                      // Switch to login
                      setIsLogin(true);
                    }
                  }}
                  className="auth-switch-btn"
                >
                  {isLogin ? 'Sign Up' : 'Sign In'}
                </button>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Dashboard
const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(getUser());
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [featureDialogOpen, setFeatureDialogOpen] = useState(false);
  const [issueDialogOpen, setIssueDialogOpen] = useState(false);
  const [accountSettingsOpen, setAccountSettingsOpen] = useState(false);
  const [accountSettingsTab, setAccountSettingsTab] = useState('account');
  const [referralProgramOpen, setReferralProgramOpen] = useState(false);
  const [credits, setCredits] = useState(0);
  const [trialStatus, setTrialStatus] = useState(null);
  const [trialExpired, setTrialExpired] = useState(false);

  useEffect(() => {
    // Setup Firebase auth state listener
    const setupAuthListener = async () => {
      const { auth, onAuthStateChanged } = await import('./firebase');
      
      const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
        if (!firebaseUser) {
          // No authenticated user, redirect to onboarding
          navigate('/onboarding');
          return;
        }
        
        // Check if email is verified (except for admin)
        if (!firebaseUser.emailVerified && firebaseUser.email !== 'admin@lawraai.com') {
          toast.error('Please verify your email address to access the dashboard.');
          await auth.signOut();
          navigate('/auth');
          return;
        }
        
        try {
          // Get ID token
          const idToken = await firebaseUser.getIdToken();
          
          // Sync/fetch user data from MongoDB
          const syncResponse = await axios.post(`${API}/auth/firebase-sync`, {
            firebase_uid: firebaseUser.uid,
            email: firebaseUser.email,
            full_name: firebaseUser.displayName || firebaseUser.email.split('@')[0]
          }, {
            headers: {
              Authorization: `Bearer ${idToken}`
            }
          });
          
          // Update local state
          setUser(syncResponse.data);
          setCredits(syncResponse.data.credits || 0);
          setUserData(syncResponse.data);
          
        } catch (error) {
          console.error('Error syncing user:', error);
          toast.error('Failed to load user data. Please try again.');
        }
      });
      
      return unsubscribe;
    };
    
    setupAuthListener();
    
    // Check if should open settings modal from URL parameters
    const params = new URLSearchParams(location.search);
    if (params.get('open') === 'account-settings') {
      setAccountSettingsOpen(true);
      const tab = params.get('tab');
      if (tab) {
        setAccountSettingsTab(tab);
      }
    }
  }, [navigate, location]);
  // Fetch user credits periodically
  useEffect(() => {
    const fetchCredits = async () => {
      if (!user) return;
      
      try {
        const { auth } = await import('./firebase');
        const firebaseUser = auth.currentUser;
        
        if (!firebaseUser) return;
        
        const idToken = await firebaseUser.getIdToken();
        const response = await axios.get(`${API}/auth/credits`, {
          headers: { Authorization: `Bearer ${idToken}` }
        });
        setCredits(response.data.credits);
      } catch (error) {
        console.error('Error fetching credits:', error);
      }
    };
    
    fetchCredits();
    // Refresh credits every 30 seconds
    const interval = setInterval(fetchCredits, 30000);
    return () => clearInterval(interval);
  }, [user]);

  const handleLogout = async () => {
    try {
      const { auth, signOut } = await import('./firebase');
      
      // Sign out from Firebase
      await signOut(auth);
      
      // Clear ALL state
      setUser(null);
      setCredits(0);
      setAccountSettingsOpen(false);
      
      // Clear ALL localStorage items
      clearAuthData();
      
      // Navigate to auth page
      toast.success('Logged out successfully');
      navigate('/auth');
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Logout failed');
    }
  };

  const handleToolClick = (toolPath) => {
    // Check if user has credits
    if (credits <= 0) {
      // Open Account Settings to Plans tab
      setAccountSettingsTab('plan');
      setAccountSettingsOpen(true);
      toast.error('You have 0 credits remaining. Please upgrade your plan to continue.');
      return;
    }
    // Navigate to tool if credits available
    navigate(toolPath);
  };

  // Trial system functions
  // Tool categories for filtering
  const toolCategories = {
    'Ask Lawra AI': 'workflow',
    'Document Review': 'analysis',
    'Contract Review': 'analysis', 
    'Inline Review & Editor': 'generation',
    'Compare Documents': 'analysis',
    'Due Diligence': 'analysis',
    'Legal Summarisation': 'analysis',
    'Legal Research': 'analysis',
    'Document Generation': 'generation',
    'Marketing Tools': 'generation',
    'Email Response Assistant': 'workflow',
    'Instruction & Action': 'workflow',
    'Notetaker': 'workflow',
    'NoteScore': 'workflow',
    'Debt Recovery Tools': 'generation',
    'Deposition Preparation': 'analysis'
  };

  // Filter function for tools
  const filterTool = (title, description) => {
    // First apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const matchesSearch = title.toLowerCase().includes(query) || description.toLowerCase().includes(query);
      if (!matchesSearch) return false;
    }
    
    // Then apply category filter
    if (activeFilter === 'all') return true;
    
    const toolCategory = toolCategories[title];
    return toolCategory === activeFilter;
  };

  return (
    <div className="dashboard" data-testid="dashboard">
      <MatrixBackground />
      {/* Header */}
      <div className="dashboard-header">
        <div className="header-content">
          <div className="header-left">
            <img src="/lawra-header-logo.png" alt="Lawra" className="header-logo-image" />
          </div>
          
          <div className="header-right">
            {/* Mobile-only logo */}
            <img src="/lawra-header-logo.png" alt="Lawra" className="header-logo-image mobile-only" />
            
            <a href="mailto:sales@lawra-ai.com?subject=Support%20Enquiry%20-%20Lawra%20AI&body=Hi%20Lawra%20AI%20Team%2C%0A%0AI%20would%20like%20help%20with%20the%20following%3A%0A%0ADescription%3A%0A%0AThank%20you" className="support-icon-btn" data-testid="dashboard-support-icon" aria-label="Contact Support">
              <Headset size={18} />
              <span className="support-online-dot" />
            </a>
            <span className="user-name desktop-only">
              Hello, <span className="gold-text">{user?.full_name}</span>
            </span>
            <div className="credits-display desktop-only" style={{
              background: 'rgba(212, 175, 55, 0.1)',
              border: '2px solid #D4AF37',
              padding: '0.5rem 1rem',
              borderRadius: '50px',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <span style={{ color: '#D4AF37', fontWeight: '600' }}>{credits}</span>
              <span style={{ color: '#e0e0e0', fontSize: '0.9rem' }}>credits</span>
            </div>
            
            <Button 
              data-testid="upgrade-btn"
              className="desktop-only"
              onClick={() => { setAccountSettingsTab('plan'); setAccountSettingsOpen(true); }}
              style={{
                background: 'linear-gradient(135deg, #D4AF37 0%, #E5B200 100%)',
                color: '#1a1a1a',
                border: 'none',
                fontWeight: '700',
                padding: '0.75rem 2rem',
                borderRadius: '50px',
                boxShadow: '0 4px 15px rgba(212, 175, 55, 0.4)',
                animation: 'signupPulse 2s ease-in-out infinite',
                whiteSpace: 'nowrap',
                minWidth: '100px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, #FFCE45 0%, #E5B200 100%)';
                e.currentTarget.style.boxShadow = '0 8px 30px rgba(212, 175, 55, 0.8)';
                e.currentTarget.style.transform = 'translateY(-2px) scale(1.05)';
                e.currentTarget.style.animation = 'none';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, #D4AF37 0%, #E5B200 100%)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(212, 175, 55, 0.4)';
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.animation = 'signupPulse 2s ease-in-out infinite';
              }}
            >
              Upgrade
            </Button>

            {/* Menu Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="menu-button">
                  <Menu size={20} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="menu-dropdown">
                <div className="mobile-only-menu-credits">
                  <span style={{ color: '#D4AF37', fontWeight: '700', fontSize: '1.1rem' }}>{credits}</span>
                  <span style={{ color: '#e0e0e0', fontSize: '0.85rem' }}>credits remaining</span>
                </div>
                <DropdownMenuItem onClick={() => setAccountSettingsOpen(true)}>
                  <Settings size={16} className="mr-2" />
                  Account Settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setReferralProgramOpen(true)}>
                  <Gift size={16} className="mr-2" />
                  Referral Program
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => window.location.href = 'mailto:support@lawra-ai.com?subject=Feature%20Request%20-%20Lawra%20AI&body=Hi%20Lawra%20AI%20Team%2C%0A%0AI%20would%20like%20to%20request%20the%20following%20feature%3A%0A%0AFeature%20Description%3A%0A%0AWhich%20sector%20is%20this%20for%20(if%20specific)%3A%0A%0AThank%20you'}>
                  <Lightbulb size={16} className="mr-2" />
                  Request Feature
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => window.location.href = 'mailto:support@lawra-ai.com?subject=Issue%20Report%20-%20Lawra%20AI&body=Hi%20Lawra%20AI%20Team%2C%0A%0AI%20would%20like%20to%20report%20the%20following%20issue%3A%0A%0ATool%2FFeature%20affected%3A%0A%0ADescription%20of%20the%20issue%3A%0A%0ASteps%20to%20reproduce%3A%0A1.%0A2.%0A3.%0A%0AThank%20you'}>
                  <AlertTriangle size={16} className="mr-2" />
                  Report Issue
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout}>
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Account Settings Modal */}
      <AccountSettings 
        open={accountSettingsOpen} 
        onOpenChange={setAccountSettingsOpen}
        user={user}
        credits={credits}
        defaultTab={accountSettingsTab}
        onLogout={handleLogout}
      />

      {/* Referral Program Modal */}
      <ReferralProgram 
        open={referralProgramOpen} 
        onOpenChange={setReferralProgramOpen}
        user={user}
      />

      {/* Main Content */}
      <div className="dashboard-main">
        <div className="dashboard-container">
          {/* All Tools Section */}
          <div className="tools-section">
            <div className="tools-header">
              <div className="tools-logo-header">
                <LawraLogo />
              </div>
            </div>

            {/* Search Bar - Redesigned */}
            <div className="search-bar-container">
              <Search size={20} className="search-icon" />
              <Input
                type="text"
                placeholder="Search tools..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input-redesigned"
              />
              {searchQuery && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSearchQuery('')}
                  className="search-clear-btn"
                >
                  ✕
                </Button>
              )}
            </div>
            
            {/* Filter Buttons */}
            <div className="filter-buttons-container">
              <Button
                variant={activeFilter === 'all' ? 'default' : 'outline'}
                onClick={() => setActiveFilter('all')}
                className={`filter-btn ${activeFilter === 'all' ? 'active' : ''}`}
              >
                All Tools
              </Button>
              <Button
                variant={activeFilter === 'analysis' ? 'default' : 'outline'}
                onClick={() => setActiveFilter('analysis')}
                className={`filter-btn ${activeFilter === 'analysis' ? 'active' : ''}`}
              >
                Document Analysis
              </Button>
              <Button
                variant={activeFilter === 'generation' ? 'default' : 'outline'}
                onClick={() => setActiveFilter('generation')}
                className={`filter-btn ${activeFilter === 'generation' ? 'active' : ''}`}
              >
                Document Generation
              </Button>
              <Button
                variant={activeFilter === 'workflow' ? 'default' : 'outline'}
                onClick={() => setActiveFilter('workflow')}
                className={`filter-btn ${activeFilter === 'workflow' ? 'active' : ''}`}
              >
                Workflow Tools
              </Button>
            </div>
            
            <div className="tools-grid">
              {/* Ask Lawra AI - FIRST TOOL */}
              {filterTool('Ask Lawra AI', 'Chat with Lawra for instant legal guidance on any topic') && (
              <Card 
                className="tool-card"
                onClick={() => {
                  // Trigger custom event to open floating chat
                  window.dispatchEvent(new Event('openLawraChat'));
                }}
                data-testid="ask-spencer-tool-card"
              >
                <CardHeader>
                  <div className="tool-card-header">
                    <div className="tool-card-icon">
                      <MessageSquare size={20} />
                    </div>
                    <CardTitle className="tool-card-title">Ask Lawra AI</CardTitle>
                  </div>
                  <CardDescription className="tool-card-desc">
                    Chat with Lawra for instant legal guidance on any topic
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="tool-card-features">
                    <div className="tool-feature">✓ Instant Answers</div>
                    <div className="tool-feature">✓ Legal Expertise</div>
                    <div className="tool-feature">✓ Conversational AI</div>
                  </div>
                  <Button className="tool-card-button" size="lg">
                    Ask
                  </Button>
                </CardContent>
              </Card>
              )}

              {/* Legal Summarisation - SECOND TOOL */}
              {filterTool('Legal Summarisation', 'Summarize long legal documents like judgments and reports quickly and accurately') && (
              <Card 
                className="tool-card"
                onClick={() => handleToolClick('/tools/legal-summarisation')}
                data-testid="legal-summarisation-tool-card"
              >
                <CardHeader>
                  <div className="tool-card-header">
                    <div className="tool-card-icon">
                      <BookOpen size={20} />
                    </div>
                    <CardTitle className="tool-card-title">Legal Summarisation</CardTitle>
                  </div>
                  <CardDescription className="tool-card-desc">
                    Summarize long legal documents like judgments and reports quickly and accurately
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="tool-card-features">
                    <div className="tool-feature">✓ Saves reading time</div>
                    <div className="tool-feature">✓ Enhances comprehension</div>
                    <div className="tool-feature">✓ Key points extraction</div>
                  </div>
                  <Button className="tool-card-button" size="lg">
                    Open Tool
                  </Button>
                </CardContent>
              </Card>
              )}

              {/* Document Review Tool */}
              {filterTool('Document Review', 'In-depth comprehensive analysis of any legal document contract agreement or law document') && (
              <Card 
                className="tool-card"
                onClick={() => handleToolClick('/tools/document-review')}
                data-testid="contract-review-tool-card"
              >
                <CardHeader>
                  <div className="tool-card-header">
                    <div className="tool-card-icon">
                      <FileText size={20} />
                    </div>
                    <CardTitle className="tool-card-title">Document Review</CardTitle>
                  </div>
                  <CardDescription className="tool-card-desc">
                    In-depth comprehensive analysis of any legal document, contract, agreement or law document
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="tool-card-features">
                    <div className="tool-feature">✓ Deep 5-Part Analysis</div>
                    <div className="tool-feature">✓ Custom Questions</div>
                    <div className="tool-feature">✓ Any Legal Document</div>
                  </div>
                  <Button className="tool-card-button" size="lg">
                    Open Tool
                  </Button>
                </CardContent>
              </Card>
              )}

              {/* Contract Review Scanner - NEW TOOL */}
              {filterTool('Contract Review', 'Quick contract scan focusing only on identifying problems risks and issues') && (
              <Card 
                className="tool-card"
                onClick={() => handleToolClick('/tools/contract-review')}
                data-testid="contract-scanner-tool-card"
              >
                <CardHeader>
                  <div className="tool-card-header">
                    <div className="tool-card-icon">
                      <AlertTriangle size={20} />
                    </div>
                    <CardTitle className="tool-card-title">Contract Review</CardTitle>
                  </div>
                  <CardDescription className="tool-card-desc">
                    Quick contract scan focusing only on identifying problems, risks and issues
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="tool-card-features">
                    <div className="tool-feature">✓ Fast Problem Detection</div>
                    <div className="tool-feature">✓ Risk Identification</div>
                    <div className="tool-feature">✓ Issues Flagging</div>
                  </div>
                  <Button className="tool-card-button" size="lg">
                    Open Tool
                  </Button>
                </CardContent>
              </Card>
              )}

              {/* Inline Review & Editor - NEW TOOL */}
              {filterTool('Inline Review & Editor', 'Review documents with AI-powered inline comments and editing') && (
              <Card 
                className="tool-card"
                onClick={() => handleToolClick('/tools/inline-review')}
                data-testid="inline-review-tool-card"
              >
                <CardHeader>
                  <div className="tool-card-header">
                    <div className="tool-card-icon">
                      <FileEdit size={20} />
                    </div>
                    <CardTitle className="tool-card-title">Inline Review & Editor</CardTitle>
                  </div>
                  <CardDescription className="tool-card-desc">
                    AI reviews documents with inline comments highlighting risks, edit directly, and re-review
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="tool-card-features">
                    <div className="tool-feature">✓ Inline AI Comments</div>
                    <div className="tool-feature">✓ Direct Editing</div>
                    <div className="tool-feature">✓ Re-Review Changes</div>
                  </div>
                  <Button className="tool-card-button" size="lg">
                    Open Tool
                  </Button>
                </CardContent>
              </Card>
              )}

              {/* Compare Documents - NEW TOOL */}
              {filterTool('Compare Documents', 'Compare two contracts side-by-side to identify key differences and risks') && (
              <Card 
                className="tool-card"
                onClick={() => handleToolClick('/tools/document-comparison')}
                data-testid="document-comparison-tool-card"
              >
                <CardHeader>
                  <div className="tool-card-header">
                    <div className="tool-card-icon">
                      <GitCompare size={20} />
                    </div>
                    <CardTitle className="tool-card-title">Compare Documents</CardTitle>
                  </div>
                  <CardDescription className="tool-card-desc">
                    Compare two contracts side-by-side to identify key clause differences and risks
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="tool-card-features">
                    <div className="tool-feature">✓ Speeds up review</div>
                    <div className="tool-feature">✓ Highlights risks efficiently</div>
                    <div className="tool-feature">✓ Clause comparison</div>
                  </div>
                  <Button className="tool-card-button" size="lg">
                    Open Tool
                  </Button>
                </CardContent>
              </Card>
              )}

              {/* Due Diligence - NEW TOOL */}
              {filterTool('Due Diligence', 'Analyse corporate and financial documents during transactions for comprehensive risk assessment') && (
              <Card 
                className="tool-card"
                onClick={() => handleToolClick('/tools/due-diligence')}
                data-testid="due-diligence-tool-card"
              >
                <CardHeader>
                  <div className="tool-card-header">
                    <div className="tool-card-icon">
                      <Search size={20} />
                    </div>
                    <CardTitle className="tool-card-title">Due Diligence</CardTitle>
                  </div>
                  <CardDescription className="tool-card-desc">
                    Analyse corporate and financial documents during transactions for comprehensive risk assessment
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="tool-card-features">
                    <div className="tool-feature">✓ Identifies issues faster</div>
                    <div className="tool-feature">✓ Supports risk assessment</div>
                    <div className="tool-feature">✓ Multi-document analysis</div>
                  </div>
                  <Button className="tool-card-button" size="lg">
                    Open Tool
                  </Button>
                </CardContent>
              </Card>
              )}

              {/* Document Generation */}
              {filterTool('Document Generation', 'Generate legal documents tailored to your sector and jurisdiction') && (
              <Card 
                className="tool-card"
                onClick={() => handleToolClick('/tools/document-generation')}
                data-testid="document-generation-tool-card"
              >
                <CardHeader>
                  <div className="tool-card-header">
                    <div className="tool-card-icon">
                      <FileEdit size={20} />
                    </div>
                    <CardTitle className="tool-card-title">Document Generation</CardTitle>
                  </div>
                  <CardDescription className="tool-card-desc">
                    Generate legal documents tailored to your sector and jurisdiction
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="tool-card-features">
                    <div className="tool-feature">✓ AI-Powered Drafting</div>
                    <div className="tool-feature">✓ Sector-Specific</div>
                    <div className="tool-feature">✓ Jurisdiction Aware</div>
                  </div>
                  <Button className="tool-card-button" size="lg">
                    Open Tool
                  </Button>
                </CardContent>
              </Card>
              )}

              {/* Legal Research */}
              {filterTool('Legal Research', 'Search case law statutes and legal precedents with AI assistance') && (
              <Card 
                className="tool-card"
                onClick={() => handleToolClick('/tools/legal-research')}
                data-testid="legal-research-tool-card"
              >
                <CardHeader>
                  <div className="tool-card-header">
                    <div className="tool-card-icon">
                      <BookOpen size={20} />
                    </div>
                    <CardTitle className="tool-card-title">Legal Research</CardTitle>
                  </div>
                  <CardDescription className="tool-card-desc">
                    In-depth legal research on case law, statutes, and precedents
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="tool-card-features">
                    <div className="tool-feature">✓ Case Law Analysis</div>
                    <div className="tool-feature">✓ Statutory Research</div>
                    <div className="tool-feature">✓ AI Recommendations</div>
                  </div>
                  <Button className="tool-card-button" size="lg">
                    Open Tool
                  </Button>
                </CardContent>
              </Card>
              )}

              {/* Deposition & Witness Preparation */}
              {filterTool('Deposition & Witness Preparation', 'Analyse transcripts and prior statements to identify inconsistencies and predict questioning lines') && (
              <Card 
                className="tool-card"
                onClick={() => handleToolClick('/tools/deposition-prep')}
                data-testid="deposition-prep-tool-card"
              >
                <CardHeader>
                  <div className="tool-card-header">
                    <div className="tool-card-icon">
                      <Gavel size={20} />
                    </div>
                    <CardTitle className="tool-card-title">Deposition & Witness Preparation</CardTitle>
                  </div>
                  <CardDescription className="tool-card-desc">
                    Analyse transcripts and prior statements to identify inconsistencies and predict questioning lines
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="tool-card-features">
                    <div className="tool-feature">✓ Helps prepare targeted questions</div>
                    <div className="tool-feature">✓ Anticipate weaknesses</div>
                    <div className="tool-feature">✓ Inconsistency detection</div>
                  </div>
                  <Button className="tool-card-button" size="lg">
                    Open Tool
                  </Button>
                </CardContent>
              </Card>
              )}

              {/* Debt Recovery Tools */}
              {filterTool('Debt Recovery Tools', 'Automate debt recovery letters and legal notices') && (
              <Card 
                className="tool-card"
                onClick={() => handleToolClick('/tools/debt-recovery')}
                data-testid="debt-recovery-tool-card"
              >
                <CardHeader>
                  <div className="tool-card-header">
                    <div className="tool-card-icon">
                      <FileText size={20} />
                    </div>
                    <CardTitle className="tool-card-title">Debt Recovery Tools</CardTitle>
                  </div>
                  <CardDescription className="tool-card-desc">
                    Automate debt recovery letters and legal notices
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="tool-card-features">
                    <div className="tool-feature">✓ 5 Letter Types</div>
                    <div className="tool-feature">✓ Auto-Generate</div>
                    <div className="tool-feature">✓ Instant Download</div>
                  </div>
                  <Button className="tool-card-button" size="lg">
                    Open Tool
                  </Button>
                </CardContent>
              </Card>
              )}

              {/* Instruction & Action */}
              {filterTool('Instruction & Action', 'Transform client instructions into structured actions') && (
              <Card 
                className="tool-card"
                onClick={() => handleToolClick('/tools/instruction-action')}
                data-testid="instruction-action-tool-card"
              >
                <CardHeader>
                  <div className="tool-card-header">
                    <div className="tool-card-icon">
                      <Clipboard size={20} />
                    </div>
                    <CardTitle className="tool-card-title">Instruction & Action</CardTitle>
                  </div>
                  <CardDescription className="tool-card-desc">
                    Transform client instructions into structured actions and documents
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="tool-card-features">
                    <div className="tool-feature">✓ Audio Transcription</div>
                    <div className="tool-feature">✓ Structured Extraction</div>
                    <div className="tool-feature">✓ Smart Triage</div>
                    <div className="tool-feature">✓ Auto Document Generation</div>
                  </div>
                  <Button className="tool-card-button" size="lg">
                    Open Tool
                  </Button>
                </CardContent>
              </Card>
              )}

              {filterTool('Notetaker', 'Record and analyze legal conversations') && (
              <Card 
                className="tool-card"
                onClick={() => navigate('/notetaker')}
                data-testid="notetaker-tool-card"
              >
                <CardHeader>
                  <div className="tool-card-header">
                    <div className="tool-card-icon">
                      <Mic size={20} />
                    </div>
                    <CardTitle className="tool-card-title">Notetaker</CardTitle>
                  </div>
                  <CardDescription className="tool-card-desc">
                    Record conversations, generate transcripts, and get AI action recommendations
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="tool-card-features">
                    <div className="tool-feature">✓ Live Recording</div>
                    <div className="tool-feature">✓ Auto Transcription</div>
                    <div className="tool-feature">✓ AI Analysis</div>
                    <div className="tool-feature">✓ Action Recommendations</div>
                  </div>
                  <Button className="tool-card-button" size="lg">
                    Open Tool
                  </Button>
                </CardContent>
              </Card>
              )}

              {filterTool('Email Response Assistant', 'AI-powered professional email responses') && (
              <Card 
                className="tool-card"
                onClick={() => handleToolClick('/tools/email-response')}
                data-testid="email-response-tool-card"
              >
                <CardHeader>
                  <div className="tool-card-header">
                    <div className="tool-card-icon">
                      <Mail size={20} />
                    </div>
                    <CardTitle className="tool-card-title">Email Response Assistant</CardTitle>
                  </div>
                  <CardDescription className="tool-card-desc">
                    Generate professional email responses with AI assistance
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="tool-card-features">
                    <div className="tool-feature">✓ AI Draft Responses</div>
                    <div className="tool-feature">✓ Professional Tone</div>
                    <div className="tool-feature">✓ Contextual Analysis</div>
                  </div>
                  <Button className="tool-card-button" size="lg">
                    Open Tool
                  </Button>
                </CardContent>
              </Card>
              )}

              {/* More Tools Coming Card - Always shows at the end */}
              <Card 
                className="tool-card more-tools-card"
                style={{
                  background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.1) 0%, rgba(184, 134, 11, 0.05) 100%)',
                  border: '2px dashed rgba(212, 175, 55, 0.3)',
                }}
              >
                <CardHeader>
                  <div className="tool-card-header">
                    <div className="tool-card-icon" style={{ background: 'rgba(212, 175, 55, 0.2)' }}>
                      <Lightbulb size={20} style={{ color: '#D4AF37' }} />
                    </div>
                    <CardTitle className="tool-card-title" style={{ color: '#D4AF37' }}>
                      More Tools Coming Soon
                    </CardTitle>
                  </div>
                  <CardDescription className="tool-card-desc">
                    We're constantly adding new features and tools to help you work smarter. Stay tuned!
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="tool-card-features">
                    <div className="tool-feature" style={{ opacity: 0.7 }}>Daily Updates</div>
                    <div className="tool-feature" style={{ opacity: 0.7 }}>Your Ideas</div>
                    <div className="tool-feature" style={{ opacity: 0.7 }}>Coming Soon</div>
                  </div>
                  <Button 
                    className="tool-card-button" 
                    size="lg"
                    data-testid="request-feature-btn"
                    onClick={() => window.location.href = 'mailto:support@lawra-ai.com?subject=Feature%20Request%20-%20Lawra%20AI&body=Hi%20Lawra%20AI%20Team%2C%0A%0AI%20would%20like%20to%20request%20the%20following%20feature%3A%0A%0AFeature%20Description%3A%0A%0AWhich%20sector%20is%20this%20for%20(if%20specific)%3A%0A%0AThank%20you'}
                    style={{
                      background: 'linear-gradient(135deg, #D4AF37 0%, #B8860B 100%)',
                      color: '#1a1a1a',
                      cursor: 'pointer',
                      pointerEvents: 'auto',
                      position: 'relative',
                      zIndex: 10
                    }}
                  >
                    Request Feature
                  </Button>
                </CardContent>
              </Card>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Onboarding Page
const OnboardingPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(false);

  const handleComplete = async (userData) => {
    setLoading(true);
    
    try {
      const { auth, createUserWithEmailAndPassword, sendEmailVerification } = await import('./firebase');
      
      // Register with Firebase
      const userCredential = await createUserWithEmailAndPassword(auth, userData.email, userData.password);
      const firebaseUser = userCredential.user;
      
      // Send email verification
      await sendEmailVerification(firebaseUser);
      
      // Get ID token
      const idToken = await firebaseUser.getIdToken();
      
      // Sync with MongoDB for credits
      const syncResponse = await axios.post(`${API}/auth/firebase-sync`, {
        firebase_uid: firebaseUser.uid,
        email: userData.email,
        full_name: userData.name,
        country: userData.country,
        jurisdiction: userData.jurisdiction
      }, {
        headers: {
          Authorization: `Bearer ${idToken}`
        }
      });
      
      // Save user data
      setUserData(syncResponse.data);
      
      toast.success(`Welcome, ${userData.name}! A verification email has been sent to ${userData.email}. Please verify your email to access all features.`);
      
      // Sign out user after registration to force email verification
      await auth.signOut();
      
      // Redirect to auth page with message
      navigate('/auth', { state: { message: 'Please check your email and verify your account before logging in.' } });
    } catch (error) {
      setLoading(false);
      const errorMessage = error.code === 'auth/email-already-in-use'
        ? 'Email already in use. Please try logging in.'
        : error.code === 'auth/weak-password'
        ? 'Password should be at least 6 characters'
        : error.message || 'Registration failed. Please try again.';
      toast.error(errorMessage);
    }
  };

  const handleGoogleSignUp = async (country, jurisdiction) => {
    setLoading(true);
    
    try {
      const { auth, googleProvider, signInWithPopup } = await import('./firebase');
      
      console.log('Starting Google sign-up...');
      
      // Sign in with Google
      const result = await signInWithPopup(auth, googleProvider);
      const firebaseUser = result.user;
      
      console.log('Google sign-up successful:', firebaseUser.email);
      
      // Get ID token
      const idToken = await firebaseUser.getIdToken();
      
      // Sync with MongoDB (Google users are automatically verified)
      const syncResponse = await axios.post(`${API}/auth/firebase-sync`, {
        firebase_uid: firebaseUser.uid,
        email: firebaseUser.email,
        full_name: firebaseUser.displayName || firebaseUser.email.split('@')[0],
        country: country,
        jurisdiction: jurisdiction
      }, {
        headers: {
          Authorization: `Bearer ${idToken}`
        }
      });
      
      // Save user data
      setUserData(syncResponse.data);
      
      // Check if user already existed (credits_used > 0) or is new
      const isExistingUser = syncResponse.data.credits_used > 0;
      const welcomeMessage = isExistingUser 
        ? `Welcome back, ${firebaseUser.displayName || 'there'}!`
        : `Welcome, ${firebaseUser.displayName || 'there'}! You've received 20 free credits.`;
      
      toast.success(welcomeMessage);
      
      // Navigate directly to dashboard (Google accounts are pre-verified)
      navigate('/dashboard');
    } catch (error) {
      setLoading(false);
      console.error('Google sign-up error:', error);
      
      const errorMessage = error.code === 'auth/popup-closed-by-user'
        ? 'Sign-up cancelled'
        : error.code === 'auth/popup-blocked'
        ? 'Pop-up blocked. Please allow pop-ups for this site.'
        : error.code === 'auth/account-exists-with-different-credential'
        ? 'An account already exists with this email. Please try logging in.'
        : error.message || 'Google sign-up failed';
      toast.error(errorMessage);
    }
  };

  return <Onboarding onComplete={handleComplete} onGoogleSignUp={handleGoogleSignUp} loading={loading} />;
};

// Tool Wrapper Component - adds consistent header to all tools
const ToolWrapper = ({ children }) => {
  const navigate = useNavigate();
  const { user: authUser, firebaseUser, loading: authLoading } = useAuth();

  // Handle redirect if not authenticated (use useEffect to avoid setState during render)
  useEffect(() => {
    if (!authLoading && !firebaseUser) {
      navigate('/onboarding');
    }
  }, [authLoading, firebaseUser, navigate]);

  // Show loading state while auth initializes
  if (authLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        background: '#0a0a0a'
      }}>
        <div style={{ color: '#D4AF37', fontSize: '1.2rem' }}>Loading...</div>
      </div>
    );
  }

  // Don't render children if not authenticated
  if (!firebaseUser) {
    return null;
  }

  const handleLogout = async () => {
    try {
      const { auth, signOut } = await import('./firebase');
      await signOut(auth);
      clearAuthData();
      toast.success('Logged out successfully');
      navigate('/auth');
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Logout failed');
    }
  };

  const handleRequestFeature = () => {
    window.location.href = 'mailto:support@lawra-ai.com?subject=Feature%20Request%20-%20Lawra%20AI&body=Hi%20Lawra%20AI%20Team%2C%0A%0AI%20would%20like%20to%20request%20the%20following%20feature%3A%0A%0AFeature%20Description%3A%0A%0AWhich%20sector%20is%20this%20for%20(if%20specific)%3A%0A%0AThank%20you';
  };

  const handleReportIssue = () => {
    window.location.href = 'mailto:support@lawra-ai.com?subject=Issue%20Report%20-%20Lawra%20AI&body=Hi%20Lawra%20AI%20Team%2C%0A%0AI%20would%20like%20to%20report%20the%20following%20issue%3A%0A%0ATool%2FFeature%20affected%3A%0A%0ADescription%20of%20the%20issue%3A%0A%0ASteps%20to%20reproduce%3A%0A1.%0A2.%0A3.%0A%0AThank%20you';
  };

  return (
    <>
      <MatrixBackground />
      <SiteHeader 
        user={authUser}
        trialUser={null}
        onLogout={handleLogout}
        onRequestFeature={handleRequestFeature}
        onReportIssue={handleReportIssue}
      />
      
      {children}
    </>
  );
};

// Main App Component
function App() {
  return (
    <AuthProvider>
      <div className="App">
        <BrowserRouter>
          <ScrollToTop />
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/onboarding" element={<OnboardingPage />} />
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/checkout/success" element={<CheckoutSuccess />} />
            <Route path="/request-quote" element={<RequestQuote />} />
            <Route path="/use-cases" element={<UseCases />} />
            <Route path="/use-cases/:slug" element={<CaseStudy />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/credit-calculator" element={<CreditCalculator />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/tools" element={<Tools />} />
            <Route path="/terms" element={<ToolWrapper><TermsAndConditions /></ToolWrapper>} />
            <Route path="/privacy" element={<ToolWrapper><PrivacyPolicy /></ToolWrapper>} />
            <Route path="/disclaimers" element={<ToolWrapper><Disclaimers /></ToolWrapper>} />
            <Route path="/tools/document-review" element={<ToolWrapper><ContractReviewTool /></ToolWrapper>} />
            <Route path="/tools/contract-review" element={<ToolWrapper><ContractReviewScanner /></ToolWrapper>} />
            <Route path="/tools/document-comparison" element={<ToolWrapper><DocumentComparison /></ToolWrapper>} />
            <Route path="/tools/due-diligence" element={<ToolWrapper><DueDiligence /></ToolWrapper>} />
            <Route path="/tools/legal-summarisation" element={<ToolWrapper><LegalSummarisation /></ToolWrapper>} />
            <Route path="/tools/deposition-prep" element={<ToolWrapper><DepositionPrep /></ToolWrapper>} />
            <Route path="/tools/inline-review" element={<ToolWrapper><InlineDocumentReview /></ToolWrapper>} />
            <Route path="/tools/email-response" element={<ToolWrapper><EmailResponseAssistant /></ToolWrapper>} />
            <Route path="/tools/instruction-action" element={<ToolWrapper><InstructionAction /></ToolWrapper>} />
            <Route path="/tools/instruction-action/output" element={<ToolWrapper><InstructionActionOutput /></ToolWrapper>} />
            <Route path="/notetaker" element={<ToolWrapper><Notetaker /></ToolWrapper>} />
            <Route path="/tools/document-generation" element={<ToolWrapper><DocumentGeneration /></ToolWrapper>} />
            <Route path="/tools/legal-research" element={<ToolWrapper><LegalResearch /></ToolWrapper>} />
            <Route path="/tools/debt-recovery" element={<ToolWrapper><DebtRecoveryTools /></ToolWrapper>} />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
          <FloatingChatWidget />
          <Footer />
        </BrowserRouter>
        <Toaster 
          position="top-center"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#333',
              color: '#fff',
            },
            success: {
              duration: 3000,
              iconTheme: {
                primary: '#D4AF37',
                secondary: '#fff',
              },
            },
            error: {
              duration: 5000,
            },
          }}
        />
      </div>
    </AuthProvider>
  );
}

export default App;